import { MouseEvent, useContext } from 'react';

import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import { Button, Grid, List, ListItemIcon, ListItemText, Skeleton } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { observer } from 'mobx-react-lite';
import { AssetModification } from 'src/models/assets/asset-modification';
import { authStoreCtx } from 'src/stores/auth';
import { isPending } from 'src/utils/common';

import AttachmentsMenu from './AttachmentsMenu';
import CarouselThumbnail from '../../../Carousel/CarouselThumbnail';
import FileIcon from 'src/components/File/FileIcon';

interface AttachmentsListType {
  onClose: () => void;
  asset?: AssetModification;
  uploadHandler: (id: number) => void;
  handlePreview?: (args: number) => void;
  chosenAsset: AssetModification | undefined;
  isIR?: boolean;
  onDeletion?: () => void;
}

const AttachmentsList = observer((props: AttachmentsListType) => {
  const {
    asset,
    uploadHandler,
    onClose,
    chosenAsset,
    handlePreview,
    isIR,
    onDeletion,
  } = props;

  if (!asset || !asset.attachments) return null;
  const { fCfg } = useContext(authStoreCtx);
  const {
    id,
    attachments: { files, attachmentsBulkDownload, deleteAttachmentReq },
  } = asset;


  const bulkDownloadHandler = async () => {
    await attachmentsBulkDownload();
  };

  const uploadFilesHandler = () => {
    uploadHandler(id);
  };

  const previewHandling =
    (event: MouseEvent<HTMLElement>) => (fileId: number) => {
      fCfg.isFilePreviewCarousel && handlePreview && handlePreview(fileId);
      event.stopPropagation();
      event.preventDefault();
    };

  return (
    <>
      <Grid container direction='column' width={isIR ? 370 : 'auto'} sx={{ height: '100%', flex: 1, minHeight: 0 }}>
        <List
          sx={{
            width: '100%',
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
          }}>
          <AttachmentsListLoading asset={asset} />
          {files?.slice().map((file) => (
            <MenuItem
              component={'a'}
              href={file.thumbnailSrc || '#'}
              key={file.id}
              onClick={(event: MouseEvent<HTMLElement>) => previewHandling(event)(file.id)}
            >
              {fCfg.isFilePreviewCarousel && fCfg.isAttachmentThumbnails ? <ListItemIcon sx={{
                  width: 92,
                  height: 68,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <CarouselThumbnail file={file} />
                </ListItemIcon>
                : <ListItemIcon sx={{ minWidth: 35 }}>
                  <FileIcon type={file.extension} />
                </ListItemIcon>}
              <ListItemText
                primaryTypographyProps={{
                  style: {
                    paddingLeft: 2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 300,
                  },
                }}
                primary={file.fileName}
              />
              <AttachmentsMenu
                fileId={file.id}
                onClose={onClose}
                downloadPending={isPending(file.filesReq)}
                deletePending={isPending(deleteAttachmentReq)}
                handlePreview={handlePreview}
                chosenAsset={chosenAsset}
                onDeletion={onDeletion}
              />
            </MenuItem>
          ))}
        </List>

        <Grid
          container
          alignItems='center'
          justifyContent='space-between'
          sx={{
            borderTop: '1px solid',
            borderColor: 'text.disabled',
          }}>
          <Button
            variant='text'
            color='primary'
            disabled={
              !chosenAsset?.canUpdate || !chosenAsset?.isRelatedEntityActive
            }
            startIcon={<UploadIcon />}
            onClick={uploadFilesHandler}>
            Upload Files
          </Button>
          <Button
            variant='text'
            color='primary'
            startIcon={<DownloadIcon />}
            onClick={bulkDownloadHandler}>
            Download All
          </Button>
        </Grid>
      </Grid>
    </>
  );
});

const AttachmentsListLoading = ({ asset }: { asset: AssetModification }) => {
  if (asset?.attachments?.attachmentsReq?.state !== 'fulfilled') {
    return (
      <>
        <Skeleton
          variant='text'
          height={60}
          sx={{
            m: theme => theme.spacing(1, 2),
          }}
        />
        <Skeleton
          variant='text'
          height={60}
          sx={{
            m: theme => theme.spacing(1, 2),
          }}
        />
        <Skeleton
          variant='text'
          height={60}
          sx={{
            m: theme => theme.spacing(1, 2),
          }}
        />
      </>
    );
  }
  return null;
};

export default AttachmentsList;
